<template>
  <button
    :disabled="!isReady"
    class="support-button"
    type="button"
    @click="openSupportChat"
  >
    <img class="support-button__img" src="@/assets/Group.svg" alt="support-button" />
    {{ label }}
  </button>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

import { useZendeskScript } from '@/composables/useZendeskScript'
import TrackingService from '@/services/TrackingService'
import ZendeskService from '@/services/ZendeskService'
import { ActionType, SourceObjectKey, EventSubtypes, SourceObjectValueElementSubtype } from '@/types/events'

withDefaults(defineProps<{ label?: string }>(), { label: 'Instant Support' })

const isReady = ref(false)
const zendeskScript = useZendeskScript()

const openSupportChat = (): void => {
  ZendeskService.openChat()
  TrackingService.trackComponentInteraction(
    ActionType.Click,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.SupportPopup,
      },
    ],
    EventSubtypes.DescriptorPage,
  )
}

const initializeZendesk = (): void => {
  ZendeskService.init(zendeskScript.value)
  isReady.value = true
}

onMounted(() => {
  if (ZendeskService.isInitialized) {
    isReady.value = true

    return
  }

  if (document.readyState === 'complete') {
    initializeZendesk()
  } else {
    window.addEventListener('load', initializeZendesk)
  }
})

onUnmounted(() => {
  window.removeEventListener('load', initializeZendesk)
})
</script>

<style scoped lang="scss">
.support-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 8px;
  cursor: pointer;
  transition: background-color 0.3s, border-color 0.3s;
  color: #fff;
  border: none;
  border-radius: var(--button-corner-radius, 8px);
  background: #333;
  font-size: 16px;
  font-weight: 700;
  line-height: var(--line-height);

  &:focus {
    background-color: #525252;
  }

  &:hover {
    border-radius: var(--button-corner-radius, 8px);
    background-color: #141414;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &__img {
    margin-right: 8px;
  }
}
</style>
